import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Edit3, Heart, HeartOff, X, Camera } from 'lucide-react';

const LEVEL_META = {
  room:      { emoji: '🏠', label: 'Rum', color: '#FFC8DD', border: '#D06892' },
  container: { emoji: '📦', label: 'Förvaring', color: '#A2D2FF', border: '#4A8BC7' },
  item:      { emoji: '✨', label: 'Sak', color: '#FFE8A3', border: '#E0A330' },
};

const STATUS_META = {
  keep:    { emoji: '💗', label: 'Behåller', color: '#B9FBC0' },
  donate:  { emoji: '🎁', label: 'Skänker', color: '#A2D2FF' },
  discard: { emoji: '🗑️', label: 'Slänger', color: '#FFE8A3' },
  unsure:  { emoji: '🤔', label: 'Osäker', color: '#E4D6F5' },
};

export default function KonMariNodeRow({ node, childrenByParent, depth, statsByRoom, onAddChild, onEdit, onDelete, onToggleJoy }) {
  const [open, setOpen] = useState(depth === 0);
  const [showPhoto, setShowPhoto] = useState(false);
  const subNodes = childrenByParent.get(node.id) || [];
  const hasChildren = subNodes.length > 0;
  const meta = LEVEL_META[node.level];
  const statsForRoom = node.level === 'room' ? (statsByRoom && statsByRoom[node.id]) : null;
  const nextLevel = node.level === 'room' ? 'container' : node.level === 'container' ? 'item' : null;
  const addLabel = node.level === 'room' ? 'Lägg till förvaring' : node.level === 'container' ? 'Lägg till sak' : null;
  const statusMeta = node.level === 'item' && node.status ? STATUS_META[node.status] : null;

  let joyIcon = null;
  if (node.level === 'item') {
    if (node.spark_joy === true) joyIcon = <Heart className="w-4 h-4 fill-[#D06892] text-[#D06892]" />;
    else if (node.spark_joy === false) joyIcon = <HeartOff className="w-4 h-4 text-gray-400" />;
    else joyIcon = <Heart className="w-4 h-4 text-gray-300" />;
  }

  const nameClass = 'text-sm flex-1 truncate'
    + (node.level === 'room' ? ' font-bold' : '')
    + (node.level === 'item' && node.spark_joy === false ? ' opacity-60' : '');

  return (
    <div data-testid={'konmari-node-' + node.id}>
      <div
        className="flex items-center gap-2 py-2 px-2 rounded-md hover:bg-black/5 group transition-colors border-b border-black/5"
        style={{ paddingLeft: (depth * 18) + 'px' }}
      >
        {hasChildren ? (
          <button onClick={() => setOpen(!open)} className="shrink-0 p-0.5 hover:bg-black/10 rounded" data-testid={'konmari-toggle-' + node.id}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        <span
          className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
          style={{ backgroundColor: meta.color + '80', color: 'rgba(0,0,0,0.7)', fontSize: '8px' }}
        >
          {meta.label}
        </span>

        <span className="text-base shrink-0">{node.emoji || meta.emoji}</span>
        {(node.photo || node.goal_photo) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowPhoto(true); }}
            className="shrink-0 relative w-7 h-7 rounded-md overflow-hidden border-2 border-[#D06892]/40 hover:border-[#D06892] transition-all"
            title={node.photo && node.goal_photo ? 'Visa nu vs mål' : node.goal_photo ? 'Visa målbild' : 'Visa foto'}
            data-testid={'konmari-photo-thumb-' + node.id}
          >
            <img src={node.photo || node.goal_photo} alt={node.name} className="w-full h-full object-cover" />
            {node.goal_photo && (
              <span className="absolute bottom-0 right-0 bg-[#4DAA4D] text-white text-[8px] font-bold px-0.5 rounded-tl" title="Har målbild">🎯</span>
            )}
          </button>
        )}
        <span className={nameClass}>
          {node.name}
          {node.level === 'item' && node.qty && node.qty > 1 && (
            <span className="text-xs opacity-60 ml-1">×{node.qty}</span>
          )}
        </span>

        {node.level === 'item' && (
          <button
            onClick={() => onToggleJoy(node)}
            className="shrink-0 p-1 opacity-60 hover:opacity-100"
            data-testid={'konmari-joy-toggle-' + node.id}
          >
            {joyIcon}
          </button>
        )}

        {statusMeta && (
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 hidden sm:inline-block"
            style={{ backgroundColor: statusMeta.color }}
          >
            {statusMeta.emoji} {statusMeta.label}
          </span>
        )}

        {statsForRoom && statsForRoom.total_items > 0 && (
          <span className="text-[10px] opacity-60 shrink-0 hidden sm:inline">
            💗 {statsForRoom.joy_items}/{statsForRoom.total_items}
          </span>
        )}

        <div className="flex gap-0.5 shrink-0 opacity-30 group-hover:opacity-100 transition-opacity">
          {nextLevel && (
            <button
              onClick={() => onAddChild(node, nextLevel)}
              className="p-1 hover:bg-black/10 rounded"
              title={addLabel}
              data-testid={'konmari-add-child-' + node.id}
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onEdit(node)}
            className="p-1 hover:bg-black/10 rounded"
            title="Ändra"
            data-testid={'konmari-edit-' + node.id}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="p-1 hover:bg-red-100 rounded"
            title="Ta bort"
            data-testid={'konmari-delete-' + node.id}
          >
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      </div>

      {node.notes && open && (
        <p className="text-[11px] italic opacity-60 px-2 py-1" style={{ paddingLeft: ((depth + 1) * 18 + 30) + 'px' }}>
          📝 {node.notes}
        </p>
      )}

      {open && hasChildren && (
        <div data-testid={'konmari-children-' + node.id}>
          {subNodes.map((c) =>
            React.createElement(KonMariNodeRow, {
              key: c.id,
              node: c,
              childrenByParent,
              depth: depth + 1,
              statsByRoom,
              onAddChild,
              onEdit,
              onDelete,
              onToggleJoy,
            })
          )}
        </div>
      )}

      {showPhoto && (node.photo || node.goal_photo) && (
        <div
          className="fixed inset-0 bg-black/85 z-[80] flex items-center justify-center p-4"
          onClick={() => setShowPhoto(false)}
          data-testid={'konmari-photo-viewer-' + node.id}
        >
          <div
            className="relative max-w-6xl w-full max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-2 left-2 bg-white/90 rounded-md px-2 py-1 text-sm font-bold flex items-center gap-1 z-10">
              <Camera className="w-3 h-3" /> {node.emoji || ''} {node.name}
            </div>
            <button
              type="button"
              onClick={() => setShowPhoto(false)}
              className="absolute top-2 right-2 z-10 bg-white rounded-full shadow p-2 hover:bg-gray-100"
              data-testid={'konmari-photo-viewer-close-' + node.id}
            >
              <X className="w-5 h-5" />
            </button>

            {node.photo && node.goal_photo ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-12" data-testid={'konmari-photo-compare-' + node.id}>
                <div className="text-center">
                  <p className="text-white text-[11px] font-bold uppercase tracking-wider mb-1 opacity-80">Nu</p>
                  <img src={node.photo} alt="nu" className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl mx-auto" />
                </div>
                <div className="text-center">
                  <p className="text-white text-[11px] font-bold uppercase tracking-wider mb-1 opacity-80">Mål 🎯</p>
                  <img src={node.goal_photo} alt="mål" className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl mx-auto border-4 border-[#4DAA4D]/60" />
                </div>
              </div>
            ) : (
              <img
                src={node.photo || node.goal_photo}
                alt={node.name}
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl mx-auto"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
